/**
 * @fileoverview Componente Molecule optimizado para mostrar un ítem de negociación de cliente en el Inbox.
 * @module CustomerDealItemMC
 * @category Component - Molecule
 * @since 1.0.0
 * @version 1.1.0 - JSDoc completo, performance y ejemplos
 */

import React, { memo } from "react";
import { useTranslation } from 'react-i18next';
import IconMC from "../../../components/IconMC";
import LoadingMC from "../../../components/LoadingMC";
import { useCustomerDealItemData } from "../../../hooks/inbox/data/useCustomerDealItemData";
import { useCustomerDealItemActions } from "../../../hooks/inbox/actions/useCustomerDealItemActions";

/**
 * CustomerDealItemMC - Componente para mostrar un ítem de negociación de cliente en el Inbox.
 */
const CustomerDealItemMC = memo(({
  deal,
  options,
  regionalConfig,
  reloadDeals,
  openModal
}) => {
  const { t } = useTranslation('InboxMC');
  // Data Layer: obtiene status, currency, selectedFunnel, formatea monto y fecha
  const {
    status,
    selectedFunnel,
    formattedAmount,
    formattedDate
  } = useCustomerDealItemData({ deal, options, regionalConfig });

  // Actions Layer: loading, handleDeleteDeal
  const { loading, handleDeleteDeal } = useCustomerDealItemActions({ deal, reloadDeals });

  // UI Layer: handler para editar negociación (optimizado)
  const handleEditDeal = React.useCallback(() => {
    openModal({ ...deal, id: deal.web_id }, selectedFunnel);
  }, [deal, selectedFunnel, openModal]);

  return (
    <div className="tw-wrapper">
      <div className="tw-border tw-rounded-xl tw-px-4 tw-py-3 tw-border-gray-200">
        <div className="tw-flex tw-items-center tw-justify-between tw-text-gray-950 tw-mb-2">
          <div className="tw-text-left tw-text-xs tw-font-medium">
            {t('deal.label')}
          </div>
          <div className="tw-flex tw-items-center">
            {!loading && (
              <>
                {/* <IconMC name="eye-outline" size="base" className="tw-mr-1" /> */}
                <IconMC
                  name="edit-outline"
                  size="base"
                  className="tw-mr-1"
                  onClick={handleEditDeal}
                />
                <IconMC name="trash-2-outline" size="base" onClick={handleDeleteDeal} />
              </>
            )}
            {loading && (
              <LoadingMC loading={loading} spinnerClassName="!tw-w-4 !tw-h-4" />
            )}
          </div>
        </div>

        <div className="tw-text-xs tw-text-gray-700 tw-mb-2">
          <span>{t('deal.funnel')}</span>
          <span className="tw-ml-2">{deal.funnel_name}</span>
        </div>

        <div className="tw-text-xs tw-text-gray-700 tw-mb-2">
          <span>{t('deal.stage')}</span>
          <span className="tw-ml-2">{deal.funnel_step_name}</span>
        </div>

        {deal.name && (
          <div className="tw-text-xs tw-text-gray-700 tw-mb-2">
            <span>{t('deal.name')}</span>
            <span className="tw-ml-2">{deal.name}</span>
          </div>
        )}

        {deal.amount && (
          <div className="tw-text-xs tw-text-gray-700 tw-mb-2">
            <span>{t('deal.amount')}</span>
            <span className="tw-ml-2">{formattedAmount}</span>
          </div>
        )}

        {deal.lead_status && status && (
          <div className="tw-text-xs tw-text-gray-700 tw-mb-2 tw-flex tw-items-center">
            <span>{t('deal.status')}</span>
            <span className={`tw-ml-1 tw-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-[5px] ${status.icon.className}`}>
              <IconMC name={status.icon.iconName} size={status.icon.size} />
              <span className="tw-ml-0.5">{status.label}</span>
            </span>
          </div>
        )}

        {deal.opening_date && (
          <div className="tw-text-xs tw-text-gray-700">
            <span>{t('deal.opening_date')}</span>
            <span className="tw-ml-2">{formattedDate}</span>
          </div>
        )}
      </div>
    </div>
  );
});

CustomerDealItemMC.displayName = 'CustomerDealItemMC';

export default CustomerDealItemMC;
